/**
 * What detection is allowed to look at, and nothing about where it looks (§15).
 *
 * Detection used to be a folder on the machine the app runs on, so it was
 * written against `node:fs` and lived in the app. It is two things now: a folder
 * on the machine, and a folder somebody points a **browser** at on the account
 * page (§5.25) — where there is no `fs`, no absolute path and no `git` to run.
 * Neither of those is the interesting half. The interesting half is the three
 * hundred lines of rules that read a `package.json` and conclude `pnpm dev`, and
 * it must not exist twice: a project prefilled in the app and the same folder
 * prefilled on the account page have to agree, or an organization publishes a
 * workspace its own members' apps would have described differently.
 *
 * So the rules take a **reader** — two methods, both repo-relative — and each
 * surface brings its own. The app's is `node:fs` with a byte ceiling; the
 * account area's is a directory handle the person granted, or the file list a
 * folder input hands over.
 *
 * Everything a reader is asked for is a file the *repository* owns, and nothing
 * has vouched for any of them: they arrive with a folder somebody picked. A
 * reader is therefore allowed to answer `null` to anything — too large, not
 * there, unreadable, not permitted — and every rule here already treats that as
 * "the repository does not say".
 */

/** One entry of a directory listing. Nothing about it is filtered by the reader. */
export interface RepoEntry {
  name: string;
  directory: boolean;
}

export interface RepoReader {
  /**
   * One level of a repository-relative directory — `''` is the root, otherwise
   * `/`-separated whatever the host's separator is.
   *
   * Dotfiles and build output are **kept**: which of them matter is a question
   * the rules answer differently in different places (`.env.example` is read at
   * the root and `.git/` is skipped in the tree), and a reader that filtered
   * would be deciding that for them. A directory that is absent or unreadable
   * is an empty listing, never a throw.
   */
  list(dir: string): Promise<readonly RepoEntry[]>;
  /**
   * A repository-relative file as text, or `null` when it is absent, too large
   * or unreadable. A reader is expected to cap what it reads: nothing past the
   * first few kilobytes of a manifest means anything here, and a truncated read
   * parses as malformed — which is already how every rule treats a file it
   * cannot make sense of.
   */
  read(path: string): Promise<string | null>;
}

/** The same file as JSON, or `null` when it is absent or does not parse. */
export async function readJson(reader: RepoReader, path: string): Promise<Record<string, unknown> | null> {
  const text = await reader.read(path);
  if (text === null) return null;
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Every name at one level, directories included — what the root listing is read as. */
export async function namesIn(reader: RepoReader, dir: string): Promise<Set<string>> {
  return new Set((await reader.list(dir)).map((entry) => entry.name));
}

/**
 * What a tree listing never descends into.
 *
 * A `dist/index.html` says what the build emitted, not what the repository is,
 * and `node_modules` would drown every pattern in someone else's files.
 */
export const SKIP: ReadonlySet<string> = new Set([
  'node_modules',
  '.git',
  'dist',
  'build',
  'target',
  'vendor',
  '.venv',
  'venv',
  '__pycache__'
]);

/**
 * The repository's own paths under `from`, relative to it and depth-capped, with
 * a trailing `/` on directories so an empty `templates/` still says its name.
 */
export async function listTree(reader: RepoReader, from: string, depth: number, cap: number): Promise<string[]> {
  const out: string[] = [];
  // `from` is `''` for the repository root, so the two halves are joined rather
  // than concatenated: `'' + '/' + 'src'` is an absolute path, and a reader
  // handed one would look outside the repository it was given.
  const at = (rel: string): string => (from ? (rel ? `${from}/${rel}` : from) : rel);
  const visit = async (prefix: string, level: number): Promise<void> => {
    if (level > depth || out.length >= cap) return;
    for (const entry of await reader.list(at(prefix))) {
      if (out.length >= cap) return;
      if (SKIP.has(entry.name) || entry.name.startsWith('.')) continue;
      const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (!entry.directory) {
        out.push(rel);
        continue;
      }
      out.push(`${rel}/`);
      await visit(rel, level + 1);
    }
  };
  await visit('', 1);
  return out;
}
